import React from "react";
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  type LucideIcon
} from "lucide-react";
import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";
import { NonEmptyVisa } from "../../types/visa.type";
import {
  getWorkflowType,
  isNoirNoidWorkflow,
  isRfeLikeWorkflow,
  WorkflowType
} from "../../types/workflow.type";

const RIGHT_ICONS = {
  FeatherArrowUpRight: ArrowUpRight,
  FeatherArrowRight: ArrowRight,
  FeatherExternalLink: ExternalLink,
  FeatherChevronRight: ChevronRight,
  FeatherChevronDown: ChevronDown
} satisfies Record<string, LucideIcon>;

export type VisaTagIconName = keyof typeof RIGHT_ICONS;

interface VisaTagProps {
  visaType: NonEmptyVisa;
  isRfe?: boolean;
  workflowType?: WorkflowType | string | null;
  /** The notice the case answers ("NOIR"), when known; wins over the workflow. */
  noticeLabel?: string | null;
  /** Shown instead of the visa, e.g. "Custom: EB-1A + Consular". */
  label?: string | null;
  size?: "xs" | "sm" | "md";
  rightIcon?: VisaTagIconName;
  rightIconVisibility?: "always" | "hover";
}

const SIZE_CLASS: Record<NonNullable<VisaTagProps["size"]>, string> = {
  xs: "h-4.5 text-xs",
  sm: "",
  md: ""
};

/** A visa type as a neutral `Tag`; status, not the visa, carries color. */
export const VisaTag: React.FC<VisaTagProps> = ({
  visaType,
  isRfe,
  workflowType,
  noticeLabel,
  label,
  size = "md",
  rightIcon,
  rightIconVisibility = "always"
}) => {
  const normalizedWorkflowType = getWorkflowType(workflowType, isRfe);
  const isRfeLike = isRfeLikeWorkflow(normalizedWorkflowType);
  const notice =
    noticeLabel ||
    (isRfeLike
      ? isNoirNoidWorkflow(normalizedWorkflowType)
        ? "NOID/NOIR"
        : "RFE"
      : null);
  const RightIcon = rightIcon ? RIGHT_ICONS[rightIcon] : null;

  return (
    <Tag
      data-visa={visaType || undefined}
      className={cn(
        "align-top",
        SIZE_CLASS[size],
        RightIcon && rightIconVisibility === "hover" && "group"
      )}
    >
      <span className="truncate">
        {label || visaType || "N/A"}
        {notice ? ` ${notice}` : ""}
      </span>
      {RightIcon ? (
        <span
          className={cn(
            "-ml-1 flex items-center overflow-hidden transition-[width,opacity,margin-left] duration-150 ease-out",
            rightIconVisibility === "hover"
              ? "w-0 opacity-0 group-hover:ml-0 group-hover:w-3 group-hover:opacity-100 group-focus-visible:ml-0 group-focus-visible:w-3 group-focus-visible:opacity-100"
              : "ml-0 w-3 opacity-100"
          )}
          aria-hidden={true}
        >
          <RightIcon />
        </span>
      ) : null}
    </Tag>
  );
};
